<script>
import { mapGetters } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import ConversationHeader from './ConversationHeader.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import { setTabBaseTitle } from 'dashboard/helper/AudioAlerts/tabActivityAlert';

export default {
  components: {
    ConversationHeader,
    EmptyState,
    MessagesView,
  },
  setup() {
    const { width: windowWidth } = useWindowSize();
    return { windowWidth };
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    // Móvil tipo WhatsApp (Victor 2026-06-02): en celular (< 768px) el chat va a
    // pantalla completa, así que siempre hace falta la flecha de volver a la lista.
    isMobile() {
      return this.windowWidth < 768;
    },
    // Título de la pestaña del navegador = nombre del cliente del chat abierto
    // (Victor 2026-06-04 · Propuesta 3). Cada pestaña muestra su propio cliente.
    tabClientName() {
      if (!this.currentChat || !this.currentChat.id) return null;
      const name = this.currentChat.meta?.sender?.name;
      return name ? `${name} · CRM` : null;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
    tabClientName: {
      immediate: true,
      handler(title) {
        setTabBaseTitle(title);
      },
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  beforeUnmount() {
    // Al salir de la conversación, devolver el título normal de la pestaña.
    setTabBaseTitle(null);
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="(isOnExpandedLayout || isMobile) && !isInboxView"
      class="border-b border-b-n-weak !pt-2"
    />
    <!-- Dashboard Apps tabs hidden -->
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <!-- Dashboard App frames hidden -->
  </div>
</template>
